import { Permissions } from './../../../core/constants/permissions';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DropdownInputComponent } from 'app/shared/dropdown-input/dropdown-input.component';
import { TrialService } from 'app/core/services/trial.service';
import { TranslationService } from 'app/core/services/translation.service';
import { LogHelper } from 'app/core/helpers/log.helper';
import { AlertService } from 'app/shared/alert/alert.service';
import { TemplateService } from 'app/core/services/template.service';
import { ModalComponent } from 'app/shared/modal/modal.component';
import { TrialConstants } from 'app/core/constants/trial-constants';
import { Countries } from 'app/core/constants/countries';
import { TrialCreate } from '../../../core/services/interfaces/trial-create.interface';
import { Cultures } from 'app/core/constants/cultures';
import { GlobalConstants } from 'app/core/constants/global-constants';
import { environment } from 'environments/environment';
import { Currencies } from 'app/core/constants/currency';
import {
  AutosuggestDropdownInputComponent
} from 'app/shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component';
import { SelectOption } from "../../../core/models/select-option.model";
import { ApiConsumerService } from "../../../core/services/api-consumer.service";
import { ApiConsumerSimple } from "../../../core/models/api-consumer-simple.model";
import { BrandService } from "../../../core/services/brand.service";
import { Brand } from "../../../core/models/brand.model";
import { noWhitespaceValidator, requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";
import { FeatureService } from "../../../core/services/feature.service";

@Component({
  selector: 'app-trial-add',
  templateUrl: './trial-add-v2.component.html',
  styleUrls: ['./trial-add.component.scss']
})
export class TrialAddComponent implements OnInit {
  @ViewChild('brandSelect') brandSelect: DropdownInputComponent;
  @ViewChild('languageSelect') languageSelect: DropdownInputComponent;
  @ViewChild('visitCountElement') visitCountElement: DropdownInputComponent;
  @ViewChild('addLanguageModal') addLanguageModal: ModalComponent;
  @ViewChild('translationVersionDropdown') translationVersionDropdown: DropdownInputComponent;
  @ViewChild('removeLanguageModal') removeLanguageModal: ModalComponent;
  @ViewChild('therapeuticAreaSelect') therapeuticAreaSelect: DropdownInputComponent;
  @ViewChild('baseCurrencySelect') baseCurrencySelect: AutosuggestDropdownInputComponent;

  emailTemplateOptions: { id: string, value: string }[] = [{ id: '0', value: "PatientGo" }, { id: '1', value: "StudyKik" }];
  expenseCategories: { id: string, value: string }[] = [];
  currencyOptions: { value: string; text: string }[] = [];
  languageSelectForm: UntypedFormGroup;
  defaultForm: UntypedFormGroup;
  forms: UntypedFormGroup[] = [];
  visitCountOptions = Array<{ value: string, text: string }>();
  versionOptions: { value: string, text: string }[] = [];
  brandOptions: { value: string, text: string }[] = [];

  allCultures: { value: string, text: string }[] = [];
  culturesInUse: { value: string, text: string }[] = [];
  currentCulture = 'en';
  defaultCoordinatorEmail = environment.defaultCoordinatorEmail;
  trialDashboardEnabled = TrialConstants.trialDashboardEnabled;
  therapeuticAreaOptions = TrialConstants.therapeuticAreaOptions;
  countries = Countries.all();
  selectedCountries: { code: string, name: string }[] = [];
  Permissions = Permissions;
  apiConsumerOptions: SelectOption[] = [];
  // Ids of the third-party consumers, so the selection rules do not need to re-query the service. AB#34875.
  thirdPartyConsumerIds = new Set<string>();
  brands: Brand[] = [];

  isRequired(controlName: string): boolean {
    const control = this.defaultForm?.get(controlName);
    if (!control || !control.validator) return false;
    const errors = control.validator({value: ''} as any);
    return errors != null && (errors['required'] === true || errors['whitespace'] === true);
  }

  constructor(
    private brandService: BrandService,
    private trialService: TrialService,
    private apiConsumerService: ApiConsumerService,
    private translationService: TranslationService,
    private alertService: AlertService,
    private router: Router,
    private templateService: TemplateService,
    protected featureService: FeatureService,
    private cdr: ChangeDetectorRef) {
    for (let culture of Cultures.all()) {
      this.allCultures.push({
        value: culture.culture,
        text: culture.name
      });

      // Add english as a culture in use (default)
      if (culture.culture === 'en') {
        this.culturesInUse.push({
          value: culture.culture,
          text: culture.name
        });
      }
    }
  }

  ngOnInit() {
    this.templateService.showHeader();

    this.loadBrandOptions();

    // Create options 1..50 for the visit count dropdown
    for (let i = 1; i <= 50; i++) {
      this.visitCountOptions.push({
        value: i.toString(),
        text: i.toString()
      });
    }

    // Build the add new trial form
    this.defaultForm = new UntypedFormGroup({
      isBusy: new FormControl<boolean>(false),
      brandId: new UntypedFormControl('', Validators.required),
      culture: new UntypedFormControl('en'),
      code: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(50)]),
      internalCode: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(50)]),
      name: new UntypedFormControl({ value: '', disabled: true }, [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
      client: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
      sponsor: new UntypedFormControl('', noWhitespaceValidator()),
      nickname: new UntypedFormControl('', [noWhitespaceValidator(), Validators.maxLength(200)]),
      opportunityNumber: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
      protocolNumber: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(50)]),
      indications: new UntypedFormControl('', [noWhitespaceValidator(),Validators.maxLength(1024)]),
      therapeuticArea: new UntypedFormControl(''),
      visitCount: new UntypedFormControl(1),
      description: new UntypedFormControl('', [noWhitespaceValidator(), Validators.maxLength(500)]),
      expensePolicy: new UntypedFormControl('', [Validators.maxLength(5000)]),
      expenseCategories: new UntypedFormControl([]),
      allowTravelRequests: new UntypedFormControl(false),
      allowExpenseRequests: new UntypedFormControl(false),
      allowActivityCentre: new UntypedFormControl(false),
      caxtonEnabled: new UntypedFormControl(true),
      inAppBankDetails: new UntypedFormControl(false),
      enableSmartNotifications: new UntypedFormControl(false),
      rideHealthEnabled: new UntypedFormControl(false),
      dashboardEnabled: new UntypedFormControl(true),
      allowFeedbackRequests: new UntypedFormControl(false),
      translationVersionId: new UntypedFormControl('Default', Validators.required),
      termsUrl: new UntypedFormControl(this.trialService.defaultTermsUrl, [requiredNoWhitespaceValidator(), Validators.pattern(GlobalConstants.urlValidatorPattern), Validators.maxLength(1024)]),
      privacyPolicyUrl: new UntypedFormControl(this.trialService.defaultPrivacyPolicyUrl, [requiredNoWhitespaceValidator(), Validators.pattern(GlobalConstants.urlValidatorPattern), Validators.maxLength(1024)]),
      studyMailbox: new UntypedFormControl(this.defaultCoordinatorEmail, [requiredNoWhitespaceValidator(), Validators.email]),
      coordinatorEmail: new UntypedFormControl('', Validators.email),
      coordinatorName: new UntypedFormControl('', noWhitespaceValidator()),
      pmName: new UntypedFormControl('', noWhitespaceValidator()),
      pmEmail: new UntypedFormControl('', Validators.email),
      countries: new UntypedFormControl([]),
      expensePolicyReminders: new UntypedFormControl('', noWhitespaceValidator()),
      bookingEnabled: new UntypedFormControl(false),
      patientAutoCompletion: new UntypedFormControl(false),
      bankAccountEnabled: new UntypedFormControl(false),
      bankAccountSelfManagementEnabled: new UntypedFormControl(false),
      baseCurrency: new UntypedFormControl('', requiredNoWhitespaceValidator()),
      bankAccountSelfCardManagementEnabled: new UntypedFormControl(false),
      virtualCardInAppEnabled: new UntypedFormControl(false),
      nmibleBankInAppEnabled: new UntypedFormControl(false),
      muvEnabled: new UntypedFormControl(false),
      apiEnabled: new UntypedFormControl(false),
      apiConsumerIds: new UntypedFormControl([]),
      requestTravelEmailTemplate: new UntypedFormControl('1'),
      magicLinkExpiryDays: new UntypedFormControl(1),
      emailPatientOnVisitCreation: new UntypedFormControl(false),
      diaryEnabled: new UntypedFormControl(false),
      genericDiaryEnabled: new UntypedFormControl(false),
      sharepointLink: new UntypedFormControl('', noWhitespaceValidator()),
      travelAdditionalInfoEnabled: new UntypedFormControl(false),
      testDataOnly: new UntypedFormControl(false),
      faqsEnabled: new UntypedFormControl(false),
      selfServeNotificationsEnabled: new UntypedFormControl(false),
      receiptLeadExpenseEnabled: new UntypedFormControl(false),
      dailySpendExpensesEnabled: new UntypedFormControl(false),
      dailySpendExpenseAppEnabled: new UntypedFormControl(false),
      virtualCallsEnabled: new UntypedFormControl(false),
      recordingAskForConsent: new UntypedFormControl(false),
    });

    // Enable / disable the api consumer dropdown based on the api enabled checkbox
    this.defaultForm.get('apiEnabled').valueChanges.subscribe((enabled) => {
      if (enabled)
        this.defaultForm.get('apiConsumerIds').enable();
      else
        this.defaultForm.get('apiConsumerIds').disable();

      this.applyApiConsumerSelectionRules();
    });

    // When daily spend expenses is disabled, also disable daily spend expense app
    this.defaultForm.get('dailySpendExpensesEnabled').valueChanges.subscribe(enabled => {
      if (!enabled) {
        this.defaultForm.patchValue({dailySpendExpenseAppEnabled: false}, { emitEvent: false });
      }
    });

    // When daily spend expense app is enabled, also enable daily spend expenses
    this.defaultForm.get('dailySpendExpenseAppEnabled').valueChanges.subscribe(enabled => {
      if (enabled) {
        this.defaultForm.patchValue({dailySpendExpensesEnabled: true}, { emitEvent: false });
      }
    });

    this.loadAndSetApiConsumerOptions();

    this.languageSelectForm = new UntypedFormGroup({
      culture: new UntypedFormControl('en')
    });

    this.languageSelectForm.get('culture').valueChanges.subscribe(value => {
      this.currentCulture = value;
    });

    // Update country tags
    this.defaultForm.get('countries').valueChanges.subscribe(c => {
      this.selectedCountries = [];
      c.forEach(country => {
        this.selectedCountries.push(this.countries.find(c => c.code === country));
      });
    });

    // Populate Currencies drop down
    for (const currency of Currencies.all()) {
      this.currencyOptions.push({ value: currency.cc, text: currency.cc });
    }

    // Load translation versions
    this.loadTranslationVersion();

    this.listenToTrialNameValues();

    this.defaultForm.get('emailPatientOnVisitCreation').valueChanges.subscribe(emailPatient => {
      if (emailPatient) {
        this.defaultForm.get('magicLinkExpiryMinutes').addValidators([Validators.required, Validators.min(10)]);
        this.defaultForm.get('requestTravelEmailTemplate').addValidators([Validators.required]);
      } else {
        this.defaultForm.get('magicLinkExpiryMinutes').setValidators(null)
        this.defaultForm.get('requestTravelEmailTemplate').addValidators(null);
      }

      this.defaultForm.get('magicLinkExpiryMinutes').updateValueAndValidity();
      this.defaultForm.get('requestTravelEmailTemplate').updateValueAndValidity();
    });

    // Disable dashboard toggle if e-diary is enabled
    this.defaultForm.valueChanges.subscribe(changes => {
      if (this.defaultForm.value.diaryEnabled || this.defaultForm.value.genericDiaryEnabled) {
        this.defaultForm.get('dashboardEnabled').disable({ emitEvent: false });
        this.defaultForm.patchValue({dashboardEnabled: true}, { emitEvent: false });
      } else {
        this.defaultForm.get('dashboardEnabled').enable({ emitEvent: false });
      }
    });

    this.cdr.detectChanges();
  }

  onBrandChanged(brandId: string) {
    this.defaultForm.patchValue({ brandId: brandId });
  }

  loadBrandOptions() {
    this.brandService.getBrands().subscribe({
      next: brands => {
        this.brands = brands;
        this.brandOptions = brands.map(b => new SelectOption(b.id, b.name));
        const defaultBrand = brands.find(b => b.isDefault);
        if (defaultBrand) {
          // Delay to allow page time to load
          setTimeout(() => {
            this.defaultForm.get('brandId').setValue(defaultBrand.id);
            this.brandSelect.setValue(defaultBrand.id);
          }, 500);
        }
      },
      error: err => {
        LogHelper.log(err);
        this.alertService.showErrorAlert(err);
      }
    });
  }

  getBrandLogoUrl() {
    const brand = this.brands.find(b => b.id === this.defaultForm.value.brandId);
    if (!brand)
      return null;

    return brand.logoUrl;
  }

  /**
   * Retrieves and sets the api consumer options to be used in the api consumer dropdown
   */
  loadAndSetApiConsumerOptions(): void {
    this.apiConsumerService.getAllApiConsumers().subscribe({
      next: (apiConsumers: any) => {
        this.thirdPartyConsumerIds = new Set<string>(
          apiConsumers.filter((c: ApiConsumerSimple) => !c.firstPartyConsumer).map((c: ApiConsumerSimple) => c.id));

        this.apiConsumerOptions = apiConsumers.map((apiConsumer: ApiConsumerSimple) => {
          // Which party a consumer belongs to decides what may be combined with it, so it has to be on
          // the face of the option - the name alone gives the operator nothing to reason with. AB#34841
          // did this on trial edit only; creation is where the class is actually chosen. AB#34875.
          const qualifiers = [apiConsumer.firstPartyConsumer ? 'first-party' : 'third-party'];
          if (!apiConsumer.enabled) {
            qualifiers.push('temporarily disabled');
          }
          return new SelectOption(apiConsumer.id, `${apiConsumer.name} (${qualifiers.join(', ')})`);
        });

        // Re-evaluated on every change rather than once, because what is selectable depends entirely on
        // what is already selected. Subscribed here rather than at form init so it cannot run against an
        // empty thirdPartyConsumerIds, and the call below covers the state at the moment the load lands.
        this.defaultForm.get('apiConsumerIds').valueChanges
          .subscribe(() => this.applyApiConsumerSelectionRules());
        this.applyApiConsumerSelectionRules();
      }
    });
  }

  /**
   * Greys out the consumers that TrialService.ValidateApiConsumerSet would reject, so an invalid set
   * cannot be built in the first place. Mirrors trial-edit, which got this in AB#34841 while the add
   * screen was left behind - so on creation the rules were still discovered by having the save bounce.
   * AB#34875.
   *
   * A trial uses either one or more first-party consumers, or a single third-party consumer, never a
   * mixture. So one third-party selection excludes everything else, and any first-party selection
   * excludes every third-party consumer.
   *
   * This is an affordance, NOT the enforcement - TrialService.Create still validates, and must, because
   * the API is reachable without this screen.
   */
  private applyApiConsumerSelectionRules(): void {
    const selected: string[] = this.defaultForm.get('apiConsumerIds').value ?? [];
    const hasThirdParty = selected.some(id => this.thirdPartyConsumerIds.has(id));
    const hasFirstParty = selected.some(id => !this.thirdPartyConsumerIds.has(id));

    this.apiConsumerOptions = this.apiConsumerOptions.map(option => {
      const next = new SelectOption(option.value, option.text);
      // Never disable something already selected - a disabled option cannot be clicked, so doing so
      // would trap the operator with a set they can no longer undo.
      next.disabled = !selected.includes(option.value)
        && (hasThirdParty || (hasFirstParty && this.thirdPartyConsumerIds.has(option.value)));
      return next;
    });
  }

  getFormAtIndex(index: 0) {
    if (this.forms.length > index) {
      return this.forms[index];
    }

    return null;
  }

  formsValid(): boolean {
    return this.defaultForm.valid && this.forms.every((group) => group.valid);
  }

  isCultureInUse(cultureCode: string): boolean {
    let inUse = false;
    this.culturesInUse.forEach(culture => {
      if (culture.value.toLowerCase() === cultureCode.toLowerCase()) {
        inUse = true;
      }
    });

    return inUse;
  }

  loadTranslationVersion() {
    this.versionOptions = [];
    this.translationService.list().subscribe({
      next: (versions: any) => {
        versions.forEach(version => {
          this.versionOptions.push({ value: version.id, text: version.version });
        });
      }
    });
  }

  /**
   * Called when the add trial form is submitted
   */
  onFormSubmit() {
    if (this.defaultForm.valid) {
      this.defaultForm.patchValue({isBusy: true});

      const mazeDiaryEnabled = this.defaultForm.get('diaryEnabled').value;
      const genericDiaryEnabled = this.defaultForm.get('genericDiaryEnabled').value;

      if (mazeDiaryEnabled && genericDiaryEnabled) {
        this.alertService.showWarningAlert('Maze E-Diary and E-Diary cannot be both enabled on a trial, please disable one.');
        this.defaultForm.patchValue({isBusy: false});
        return;
      }

      const createTrial: TrialCreate = {
        brandId: this.defaultForm.get('brandId').value,
        trialCode: this.defaultForm.get('code').value,
        internalCode: this.defaultForm.get('internalCode').value,
        name: this.defaultForm.get('name').value,
        description: this.defaultForm.get('description').value,
        client: this.defaultForm.get('client').value,
        sponsor: this.defaultForm.get('sponsor').value,
        nickname: this.defaultForm.get('nickname').value,
        opportunityNumber: this.defaultForm.get('opportunityNumber').value,
        protocolNumber: this.defaultForm.get('protocolNumber').value,
        indications: this.defaultForm.get('indications').value,
        therapeuticArea: +this.defaultForm.get('therapeuticArea').value,
        baseCurrency: this.defaultForm.get('baseCurrency').value,
        visitCount: +this.visitCountElement.selectedValue,
        allowTravelRequests: this.defaultForm.get('allowTravelRequests').value,
        allowExpenseRequests: this.defaultForm.get('allowExpenseRequests').value,
        allowActivityCentre: this.defaultForm.get('allowActivityCentre').value,
        enableSmartNotifications: this.defaultForm.get('enableSmartNotifications').value,
        expenseCategories: this.defaultForm.get('expenseCategories').value,
        termsUrl: this.defaultForm.get('termsUrl').value,
        privacyPolicyUrl: this.defaultForm.get('privacyPolicyUrl').value,
        studyMailbox: this.defaultForm.get('studyMailbox').value,
        coordinatorEmail: this.defaultForm.get('coordinatorEmail').value,
        coordinatorName: this.defaultForm.get('coordinatorName').value,
        pmName: this.defaultForm.get('pmName').value,
        pmEmail: this.defaultForm.get('pmEmail').value,
        translationVersionId: this.versionOptions[0].value,
        rideHealthEnabled: this.defaultForm.get('rideHealthEnabled').value,
        dashboardEnabled: this.defaultForm.get('dashboardEnabled').value,
        allowFeedbackRequests: this.defaultForm.get('allowFeedbackRequests').value,
        countries: this.defaultForm.get('countries').value,
        expensePolicyReminders: this.defaultForm.get('expensePolicyReminders').value,
        bookingEnabled: this.defaultForm.get('bookingEnabled').value,
        patientAutoCompletion: this.defaultForm.get('patientAutoCompletion').value,
        bankAccountEnabled: this.defaultForm.get('bankAccountEnabled').value,
        bankAccountSelfManagementEnabled: this.defaultForm.get('bankAccountSelfManagementEnabled').value,
        bankAccountSelfCardManagementEnabled: this.defaultForm.get('bankAccountSelfCardManagementEnabled').value,
        virtualCardInAppEnabled: this.defaultForm.get('virtualCardInAppEnabled').value,
        nmibleBankInAppEnabled: this.defaultForm.get('nmibleBankInAppEnabled').value,
        muvEnabled: this.defaultForm.get('muvEnabled').value,
        apiEnabled: this.defaultForm.get('apiEnabled').value,
        apiConsumerIds: this.defaultForm.get('apiConsumerIds').value,
        requestTravelEmailTemplate: +this.defaultForm.get('requestTravelEmailTemplate').value,
        magicLinkExpiryDays: +this.defaultForm.get('magicLinkExpiryDays').value,
        emailPatientOnVisitCreation: this.defaultForm.get('emailPatientOnVisitCreation').value,
        diaryEnabled: this.defaultForm.get('diaryEnabled').value,
        genericDiaryEnabled: this.defaultForm.get('genericDiaryEnabled').value,
        sharepointLink: this.defaultForm.get('sharepointLink').value,
        travelAdditionalInfoEnabled: this.defaultForm.get('travelAdditionalInfoEnabled').value,
        testDataOnly: this.defaultForm.get('testDataOnly').value,
        faqsEnabled: this.defaultForm.get('faqsEnabled').value,
        selfServeNotificationsEnabled: this.defaultForm.get('selfServeNotificationsEnabled').value,
        receiptLeadExpenseEnabled: this.defaultForm.get('receiptLeadExpenseEnabled').value,
        dailySpendExpensesEnabled: this.defaultForm.get('dailySpendExpensesEnabled').value,
        dailySpendExpenseAppEnabled: this.defaultForm.get('dailySpendExpenseAppEnabled').value,
        virtualCallsEnabled: this.defaultForm.get('virtualCallsEnabled').value,
        recordingAskForConsent: this.defaultForm.get('recordingAskForConsent').value
      };

      this.trialService.createTrial(createTrial).subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Trial Added Successfully');
          this.defaultForm.patchValue({isBusy: false});
          void this.router.navigate(['/trial']);
        },
        error: (error) => {
          this.defaultForm.patchValue({isBusy: false});
          LogHelper.log(error);
          this.alertService.showWarningAlert(error.error ? error.error.title : 'Sorry, there was a problem!');
        }
      });
    }
  }

  onTherapeuticAreaChanged(therapeuticArea: string) {
    this.defaultForm.patchValue({ therapeuticArea: therapeuticArea });
  }

  onBaseCurrencySelected(baseCurrency: string) {
    this.defaultForm.patchValue({ baseCurrency: baseCurrency });
  }

  onConfirmedRemoveLanguage() {
    let index = -1;
    for (let i = 0; i < this.forms.length; i++) {
      if (this.forms[i].get('culture').value === this.currentCulture) {
        index = i;
      }
    }

    if (index >= 0) {
      let cultureIndex = -1;
      for (let i = 0; i < this.culturesInUse.length; i++) {
        if (this.currentCulture === this.culturesInUse[i].value) {
          cultureIndex = i;
        }
      }

      this.forms.splice(index, 1);
      this.culturesInUse.splice(cultureIndex, 1);
      this.currentCulture = 'en';
      this.languageSelectForm.patchValue({ culture: 'en' });
      this.languageSelect.setValue('en');
    }

    this.removeLanguageModal.hide();
  }

  onAddLanguage(culture: { value: string, text: string }) {
    if (!this.isCultureInUse(culture.value)) {
      this.culturesInUse.push(culture);
    }
  }

  listenToTrialNameValues() {
    this.defaultForm.get('client').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });

    this.defaultForm.get('sponsor').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });

    this.defaultForm.get('nickname').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });

    this.defaultForm.get('opportunityNumber').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });
  }

  setTrialNameValue() {
    const client = this.defaultForm.get('client').value;
    const sponsor = this.defaultForm.get('sponsor').value;
    const nickname = this.defaultForm.get('nickname').value;
    const opportunityNumber = this.defaultForm.get('opportunityNumber').value;

    const value = `${client}${client && (sponsor) ? ' / ' : ''}${sponsor}${nickname && (client || sponsor) ? ' / ' : ''}${nickname}${opportunityNumber && (client || sponsor || nickname) ? ' - ' : ''}${opportunityNumber}`;

    this.defaultForm.controls['name'].setValue(value);
  }

  removeCountryFromTrial(countryCode: string): void {
    const index = this.defaultForm.get('countries').value.indexOf(countryCode);

    if (index >= 0) {
      const countries = this.defaultForm.get('countries').value;
      countries.splice(index, 1);
      this.defaultForm.patchValue({ countries: countries });
    }
  }

  protected readonly Brand = Brand;
}
